export const WORK_EMAIL_REQUIRED_MESSAGE =
    'Please use your company or work email address. Free and disposable email providers are not accepted.';

let vendoredBlockedDomainsPromise: Promise<Set<string>> | null = null;

function vendoredBlockedDomains(): Promise<Set<string>>
{
    if (!vendoredBlockedDomainsPromise)
    {
        vendoredBlockedDomainsPromise = Promise.all([
            import('../data/emailDomains/free_domains.json'),
            import('../data/emailDomains/disposable_domains.json'),
        ]).then(([freeModule, disposableModule]) =>
            new Set<string>([
                ...(freeModule.default as string[]),
                ...(disposableModule.default as string[]),
            ]),
        );
    }
    return vendoredBlockedDomainsPromise;
}

function normalizeDomain(value: string): string
{
    return String(value || '').trim().toLowerCase().replace(/^@+/, '').replace(/\.$/, '');
}

function configuredDomains(value?: string): Set<string>
{
    return new Set(
        String(value || '')
            .replace(/;/g, ',')
            .split(',')
            .map(normalizeDomain)
            .filter(Boolean),
    );
}

function domainOrParentMatches(domain: string, candidates: Set<string>): boolean
{
    const parts = domain.split('.');
    for (let index = 0; index < Math.max(1, parts.length - 1); index += 1)
    {
        if (candidates.has(parts.slice(index).join('.')))
        {
            return true;
        }
    }
    return false;
}

function emailDomain(email: string): string
{
    const atIndex = String(email || '').lastIndexOf('@');
    return atIndex >= 0 ? normalizeDomain(email.slice(atIndex + 1)) : '';
}

function runtimeAllowedDomains(): Set<string>
{
    const runtimeValue =
        typeof window !== 'undefined'
            ? window._env_?.REACT_APP_WORK_EMAIL_ALLOWED_DOMAINS
            : undefined;
    return configuredDomains(
        runtimeValue || process.env.REACT_APP_WORK_EMAIL_ALLOWED_DOMAINS,
    );
}

function runtimeBlockedDomains(): Set<string>
{
    const runtimeValue =
        typeof window !== 'undefined'
            ? window._env_?.REACT_APP_WORK_EMAIL_BLOCKED_DOMAINS
            : undefined;
    return configuredDomains(
        runtimeValue || process.env.REACT_APP_WORK_EMAIL_BLOCKED_DOMAINS,
    );
}

export async function isNonWorkEmailAddress(email: string): Promise<boolean>
{
    const domain = emailDomain(email);
    if (!domain)
    {
        return false;
    }
    if (domainOrParentMatches(domain, runtimeAllowedDomains()))
    {
        return false;
    }

    const configuredBlockedDomains = runtimeBlockedDomains();
    try
    {
        const blockedDomains = await vendoredBlockedDomains();
        if (configuredBlockedDomains.size === 0)
        {
            return domainOrParentMatches(domain, blockedDomains);
        }

        return (
            domainOrParentMatches(domain, configuredBlockedDomains)
            || domainOrParentMatches(domain, blockedDomains)
        );
    }
    catch
    {
        // The backend repeats this validation and remains authoritative.
        return domainOrParentMatches(domain, configuredBlockedDomains);
    }
}
